import React, {useCallback} from 'react';

import moment from 'moment';
import Papa from 'papaparse';
import {useBackgroundTaskManager} from 'src/contexts/background-task-context';

import {ChunkedDownloadRequestNotification} from './chunked-download-request-notification';
import {chunkArray} from './utils';

import type {GetData} from './typedef';
import type {ReactNode} from 'react';

const DOWNLOAD_SUCCESS_MESSAGE = 'The file has been downloaded successfully';
const DOWNLOAD_FAILED_MESSAGE = 'The file has not been downloaded. Please, try again.';
const DOWNLOAD_STACKOVERFLOW_MESSAGE = 'Only one report can be downloaded at once. Please wait';

type Props<Data> = {
    children: (handler: (ids?: number[]) => void) => ReactNode,
    ids?: number[],
    taskName: string,
    fileName: string,
    fileHeaders: Record<string, string>,
    getData: GetData<Data>,
    chunkSize: number,
    onSuccess?: () => void,
    onError?: () => void,
    stackOverflowMessage?: string,
};

function triggerDownload(csv: string, fileName: string): void {
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export const ChunkedDownloadTool = <Data, >(props: Props<Data>) => {
    const {
        children,
        taskName,
        fileName,
        fileHeaders,
        getData,
        chunkSize,
        onSuccess,
        onError,
        stackOverflowMessage = DOWNLOAD_STACKOVERFLOW_MESSAGE,
    } = props;
    const createBackgroundTask = useBackgroundTaskManager();

    const generateFile = useCallback((fileData: Data[]) => {
        const csv = Papa.unparse([fileHeaders, ...fileData], {
            delimiter: ',',
            header: false,
            newline: '\r\n',
            quotes: true,
            skipEmptyLines: true,
        });
        const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
        triggerDownload(csv, `${fileName}_${timestamp}.csv`);
    }, [fileName, fileHeaders]);

    const handler = useCallback((ids?: number[]) => {
        const entityIds = ids ?? props.ids;

        if (entityIds) {
            const downloadTask = createBackgroundTask(
                taskName,
                <ChunkedDownloadRequestNotification<Data>
                    chunks={chunkArray(entityIds, chunkSize)}
                    getData={getData}
                    onDone={(data, error) => {
                        if (!downloadTask) {
                            return;
                        }
                        const {completeSuccess, completeFailure} = downloadTask;

                        if (!error) {
                            generateFile(data);
                            completeSuccess(<b>{DOWNLOAD_SUCCESS_MESSAGE}</b>);
                            onSuccess?.();
                        } else {
                            completeFailure(<b>{DOWNLOAD_FAILED_MESSAGE}</b>);
                            onError?.();
                        }
                    }}
                />,
                {
                    maxStack: 1,
                    stackOverflowMessage,
                    cancelable: true,
                    cancelMessage: 'The downloading is canceled',
                },
            );
        }
    }, [
        props.ids,
        chunkSize,
        generateFile,
        taskName,
        getData,
        createBackgroundTask,
        onSuccess,
        onError,
        stackOverflowMessage,
    ]);

    return (
        <>
            {children(handler)}
        </>
    );
};
